import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaTaxi, FaUser, FaIdCard, FaCar, FaMapMarkerAlt, FaFileUpload, FaExclamationCircle } from 'react-icons/fa';
import { API_URL } from '../../config/apiConfig';
import { useDocumentRequirements } from '../../context/DocumentRequirementsContext';

const VendorDriverAdd = () => {
  const [cabTypes, setCabTypes] = useState([]);
  const [loadingCabTypes, setLoadingCabTypes] = useState(false);
  const { documentConfig, loading: loadingDocConfig, error: docConfigError } = useDocumentRequirements();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    licenseNumber: '',
    licenseExpiry: '',
    vehicleNumber: '',
    vehicleModel: '',
    vehicleType: '',
    vehicleCapacity: '',
    emergencyContact: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    panNumber: '',
    // Documents - matching DriverRegister structure
    aadhaarCard: null,
    driversLicense: null,
    driverPhoto: null,
    vehicleRC: null,
    insuranceCertificate: null,
    pucCertificate: null,
    fitnessCertificate: null,
    routePermit: null,
    vehiclePhotoFront: null,
    vehiclePhotoBack: null
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch cab types when component mounts
  useEffect(() => {
    const fetchCabTypes = async () => {
      setLoadingCabTypes(true);
      try {
        const response = await axios.get(`${API_URL}/cabtype/all`);
        if (response.data && response.data.data) {
          const activeCabTypes = response.data.data.filter(cabType => cabType.active);
          setCabTypes(activeCabTypes);
        }
      } catch (error) {
        console.error('Error fetching cab types:', error);
        try {
          const fallbackResponse = await axios.get(`${API_URL}/cab`);
          if (fallbackResponse.data && fallbackResponse.data.data) {
            setCabTypes(fallbackResponse.data.data);
          }
        } catch (fallbackError) {
          console.error('Error fetching cab types from fallback:', fallbackError);
          toast.error('Failed to load available cab types. Please try again later.');
        }
      } finally {
        setLoadingCabTypes(false);
      }
    };

    fetchCabTypes();
  }, []);

  const handleChange = (e) => {
    // Handle document file uploads
    const documentTypes = [
      'aadhaarCard', 'driversLicense', 'driverPhoto',
      'vehicleRC', 'insuranceCertificate', 'pucCertificate',
      'fitnessCertificate', 'routePermit', 'vehiclePhotoFront', 'vehiclePhotoBack'
    ];

    if (documentTypes.includes(e.target.name)) {
      // Validate file type (only images)
      const file = e.target.files[0];
      if (file) {
        const fileType = file.type;
        if (!fileType.match(/^image\/(jpeg|jpg|png)$/)) {
          toast.error(`Only image files (JPG, JPEG, PNG) are allowed for ${e.target.name}`);
          e.target.value = '';
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`File size too large. Maximum 5MB allowed for ${e.target.name}`);
          e.target.value = '';
          return;
        }

        // Update form data with the file
        setFormData((prevState) => ({
          ...prevState,
          [e.target.name]: file
        }));

        // Show success feedback
        toast.success(`${e.target.name} selected successfully`);
      } else {
        // File was removed
        setFormData((prevState) => ({
          ...prevState,
          [e.target.name]: null
        }));
      }
    } else {
      // Handle regular form inputs
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation (reusing driver registration validation logic)
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!/^[0-9]{10}$/.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }


    // Validate vehicle type selection
    if (!formData.vehicleType) {
      toast.error('Please select a vehicle type');
      return;
    }

    // Check if all required documents are provided
    const requiredDocKeys = Object.entries(documentConfig)
      .filter(([_, config]) => config.required)
      .map(([key]) => key);
    
    const missingDocs = requiredDocKeys.filter(key => !formData[key]);
    
    if (missingDocs.length > 0) {
      const missingDocNames = missingDocs.map(key => documentConfig[key].name).join(', ');
      toast.error(`Missing required documents: ${missingDocNames}`);
      return;
    }

    try {
      setLoading(true);
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      
      // Create FormData object for file upload with explicit values
      const driverData = new FormData();
      
      // Add each field explicitly to ensure they're included
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          if (formData[key] instanceof File) {
            driverData.append(key, formData[key]);
          } else {
            driverData.append(key, formData[key]);
          }
        }
      });
      
      // Add vendor ID
      driverData.append('vendorId', vendor._id);

      const response = await axios.post(`${API_URL}/vendor/${vendor._id}/drivers/register`, driverData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status) {
        toast.success('Driver added successfully!');
        navigate('/vendor/dashboard');
      } else {
        toast.error(response.data.message || 'Failed to add driver');
      }
    } catch (error) {
      console.error('Error adding driver:', error);
      toast.error(error.response?.data?.message || 'Failed to add driver');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary flex items-center justify-center">
            <FaTaxi className="text-white text-3xl" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-gray-900">
            Add Driver to Fleet
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Register a new driver for your vendor account
          </p>
          <button
            onClick={() => navigate('/vendor/dashboard')}
            className="mt-4 text-primary hover:text-primary-dark font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <FaUser className="text-primary mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Enter driver's full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="driver@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      required
                      maxLength="10"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="10-digit phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      id="dateOfBirth"
                      required
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      id="password"
                      required
                      minLength="6"
                      value={formData.password}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Minimum 6 characters"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      id="confirmPassword"
                      required
                      minLength="6"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle & License Information */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <FaCar className="text-primary mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Vehicle & License Information</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-700">
                      Vehicle Type *
                    </label>
                    <select
                      name="vehicleType"
                      id="vehicleType"
                      required
                      value={formData.vehicleType}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    >
                      <option value="">Select Vehicle Type</option>
                      {loadingCabTypes ? (
                        <option disabled>Loading cab types...</option>
                      ) : (
                        cabTypes.map(cabType => (
                          <option key={cabType._id} value={cabType._id}>
                            {cabType.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="vehicleModel" className="block text-sm font-medium text-gray-700">
                      Vehicle Model *
                    </label>
                    <input
                      type="text"
                      name="vehicleModel"
                      id="vehicleModel"
                      required
                      value={formData.vehicleModel}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="E.g., Toyota Corolla"
                    />
                  </div>

                  <div>
                    <label htmlFor="vehicleNumber" className="block text-sm font-medium text-gray-700">
                      Vehicle Registration Number *
                    </label>
                    <input
                      type="text"
                      name="vehicleNumber"
                      id="vehicleNumber"
                      required
                      value={formData.vehicleNumber}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Enter vehicle number"
                    />
                  </div>

                  <div>
                    <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700">
                      Driver's License Number *
                    </label>
                    <input
                      type="text"
                      name="licenseNumber"
                      id="licenseNumber"
                      required
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Enter license number"
                    />
                  </div>

                  <div>
                    <label htmlFor="licenseExpiry" className="block text-sm font-medium text-gray-700">
                      License Expiry Date *
                    </label>
                    <input
                      type="date"
                      name="licenseExpiry"
                      id="licenseExpiry"
                      required
                      value={formData.licenseExpiry}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>

                </div>
              </div>
            </div>

            {/* Required Documents */}
            <div className="border-t border-gray-200 pt-8">
              <div className="flex items-center mb-6">
                <FaFileUpload className="text-primary mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Required Documents</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Please upload clear images of the following documents. All documents marked with * are mandatory.
                Only JPG, JPEG, and PNG image formats are accepted.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-yellow-800 mb-2">Personal Documents (Mandatory)</h3>
                <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1">
                  <li>Aadhaar Card - proof of identity and address</li>
                  <li>Driver's License - must be a valid commercial license</li>
                  <li>Recent passport-sized photograph</li>
                </ul>
              </div>
              
              <div className="space-y-8">
                {/* Documents Section */}
                {loadingDocConfig ? (
                  <div className="animate-pulse flex flex-col items-center py-8">
                    <div className="h-6 w-48 bg-gray-300 rounded mb-4"></div>
                    <div className="h-32 w-full bg-gray-200 rounded"></div>
                  </div>
                ) : docConfigError ? (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-100 mb-4">
                    <div className="flex items-center">
                      <FaExclamationCircle className="text-red-500 mr-2" />
                      <p>Error loading document requirements. Please try again later.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Personal Documents Section */}
                    <div>
                      <h3 className="font-medium text-gray-800 mb-4">Personal Documents</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(documentConfig)
                          .filter(([key]) => ['aadhaarCard', 'driversLicense', 'driverPhoto'].includes(key))
                          .map(([key, config]) => (
                            <div key={key} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <label htmlFor={key} className="block text-gray-700 font-medium">
                                  {config.name}
                                </label>
                                <div className="flex items-center gap-2">
                                  {formData[key] && (
                                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">
                                      ✓ Selected
                                    </span>
                                  )}
                                  {config.required && (
                                    <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                                      Required
                                    </span>
                                  )}
                                </div>
                              </div>
                              <input
                                type="file"
                                id={key}
                                name={key}
                                onChange={handleChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                                accept=".jpg,.jpeg,.png"
                                required={config.required}
                              />
                              {formData[key] && (
                                <div className="mt-2 text-sm text-green-600">
                                  📎 {formData[key].name} ({(formData[key].size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    
                    {/* Vehicle Documents Section */}
                    <div>
                      <h3 className="font-medium text-gray-800 mb-4">Vehicle Documents</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(documentConfig)
                          .filter(([key]) => ['vehicleRC', 'insuranceCertificate', 'pucCertificate', 'fitnessCertificate', 'routePermit'].includes(key))
                          .map(([key, config]) => (
                            <div key={key} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <label htmlFor={key} className="block text-gray-700 font-medium">
                                  {config.name}
                                </label>
                                <div className="flex items-center gap-2">
                                  {formData[key] && (
                                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">
                                      ✓ Selected
                                    </span>
                                  )}
                                  {config.required && (
                                    <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                                      Required
                                    </span>
                                  )}
                                </div>
                              </div>
                              <input
                                type="file"
                                id={key}
                                name={key}
                                onChange={handleChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                                accept=".jpg,.jpeg,.png"
                                required={config.required}
                              />
                              {formData[key] && (
                                <div className="mt-2 text-sm text-green-600">
                                  📎 {formData[key].name} ({(formData[key].size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    
                    {/* Vehicle Photos Section */}
                    <div>
                      <h3 className="font-medium text-gray-800 mb-4">Vehicle Photos</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(documentConfig)
                          .filter(([key]) => ['vehiclePhotoFront', 'vehiclePhotoBack'].includes(key))
                          .map(([key, config]) => (
                            <div key={key} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <label htmlFor={key} className="block text-gray-700 font-medium">
                                  {config.name}
                                </label>
                                <div className="flex items-center gap-2">
                                  {formData[key] && (
                                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">
                                      ✓ Selected
                                    </span>
                                  )}
                                  {config.required && (
                                    <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                                      Required
                                    </span>
                                  )}
                                </div>
                              </div>
                              <input
                                type="file"
                                id={key}
                                name={key}
                                onChange={handleChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                                accept=".jpg,.jpeg,.png"
                                required={config.required}
                              />
                              {formData[key] && (
                                <div className="mt-2 text-sm text-green-600">
                                  📎 {formData[key].name} ({(formData[key].size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
                  I agree to the <span className="text-primary hover:underline cursor-pointer">Terms of Service</span> and <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/vendor/dashboard')}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding Driver...
                    </span>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VendorDriverAdd;
